import {
  Button,
  Table,
  Icon,
  Tooltip,
  DatePicker,
  message,
  Popconfirm,
} from 'antd'
import moment from 'moment-timezone'
import React, { useCallback, useEffect, useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { Link, useParams } from 'react-router-dom'
import { useAsync } from '../../hooks'
import useFilters from '../../hooks/useFilters'
import {
  useOfficeChecks,
  useOfficeChecksPagination,
  useFetchOfficeChecks,
} from '../../redux/slices/office-checks'
import { USER_ROLES } from '../../lib/constants'
import { usePractice } from '../../redux/slices/practices'
import {
  useCanSign,
  useCurrentUser,
  useFeatureGuard,
} from '../../redux/slices/session'
import { toPath } from '../../routes'
import api from '../../api'
import { ListError, PageHeader } from '../Common'
import DiseasesTags from '../Common/DiseasesTags'
import Filters from '../Forms/Filters'
import { formatDOB } from '../../lib/datetime'
import useExportExcel from '../../hooks/useExportExcel'
import useQueryParams from '../../hooks/useQueryParams'
import BatchDownloadButton from './BatchDownloadButton'

const { RangePicker } = DatePicker

const useStyles = createUseStyles({
  active: {
    color: 'green',
  },
  inactive: {
    color: 'red',
  },
  criticalIcon: {
    color: 'red',
  },
  notPresent: {
    color: 'orange',
  },
  pageHeader: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: 'auto auto 1fr',
    alignItems: 'center',
  },
  datePicker: {
    marginRight: '1rem',
    marginBottom: '20px',
  },
  total: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 20,
  },
  totalResults: {
    background: ({ theme }) => theme.lightGray,
    color: '#707070',
    padding: '0 24px',
    fontWeight: 'bold',
    display: 'flex',
    alignContent: 'center',
    flexWrap: 'wrap',
  },
  officeChecksTable: {
    '& tr td:last-child': {
      textAlign: 'right',
      borderLeft: '1px solid rgb(232, 232, 232)',
      backgroundColor: '#f1f2fc',
    },
    '& tr th:last-child': {
      textAlign: 'center',
      borderLeft: '1px solid rgb(232, 232, 232)',
      backgroundColor: '#f1f2fc',
    },
    '& tr td:last-child button': {
      border: '1px solid #5175b3',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '.5rem',
    justifyContent: 'center',
  },
})

export default function OfficeChecksReports() {
  const { practiceId } = useParams()
  const practice = usePractice(practiceId)

  const user = useCurrentUser()
  const userRole = user.role
  const isPractitioner = userRole === USER_ROLES.PRACTITIONER
  const isAdmin = userRole === USER_ROLES.ADMIN

  const canSign = useCanSign()

  const theme = useTheme()
  const classes = useStyles({ theme })
  const [editDates, setEditDates] = useState({})

  const DEFAULT_FILTERS = {
    filters: 'all',
    fromDate: moment().format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
  }

  const { handlePageChange, conditions, setConditions } = useFilters({
    ...DEFAULT_FILTERS,
    practiceId,
  })

  const { page, fromDate, toDate, filters } = conditions

  const res = useAsync(
    useFetchOfficeChecks(
      practiceId,
      fromDate,
      toDate,
      page,
      filters,
      conditions,
    ),
  )

  const { pending, error, clearError, execute } = res

  const handleDateChange = useCallback(
    (_, rangeString) => {
      if (rangeString[0] === '' || rangeString[1] === '') return

      setConditions({
        ...conditions,
        fromDate: rangeString[0],
        toDate: rangeString[1],
        page: 1,
      })
    },
    [setConditions, conditions],
  )

  useEffect(() => {
    execute(
      practiceId,
      conditions.fromDate,
      conditions.toDate,
      page,
      filters,
      conditions,
    )
  }, [conditions, execute, practiceId, page, filters])

  const handleStatusFilter = useCallback(
    ({
      filters: newFilters,
      medicalProviderId,
      deviceTypeId,
      officeLocationId,
      query,
    }) => {
      setConditions({
        ...conditions,
        filters: newFilters,
        medicalProviderId,
        deviceTypeId,
        officeLocationId,
        query,
        page: 1,
      })
    },
    [conditions, setConditions],
  )

  const officeChecks = useOfficeChecks(fromDate, toDate)
  const [, , getPathWithQuery] = useQueryParams({})

  const toOfficeCheck = useCallback(
    id => {
      return getPathWithQuery(
        toPath.practices.officeChecks.officeCheck({
          id,
          practiceId,
        }),
        { ...conditions },
      )
    },
    [practiceId, conditions, getPathWithQuery],
  )

  const toEditOfficeCheck = id => {
    const path = toPath.practices.officeChecks.officeCheck.edit({
      id,
      practiceId,
    })

    return `${path}?from_date=${fromDate}&to_date=${toDate}`
  }

  const handleDelete = useCallback(
    async id => {
      try {
        await api.officeChecks.destroy(id, practiceId)
        message.success('Office Check successfully deleted')
        execute(practiceId, fromDate, toDate, page, filters, conditions)
      } catch (e) {
        message.error(`There was an error removing this report: ${e.message}`)
      }
    },
    [conditions, execute, filters, fromDate, page, practiceId, toDate],
  )

  const handleSaveDate = report => {
    const { id } = report
    const checkDate = editDates[id].toISOString()

    api.officeChecks
      .update(id, practiceId, {
        checkDate,
      })
      .then(() => {
        message.success('Report date updated successfully')
        setEditDates({ ...editDates, [id]: undefined })
        execute(practiceId, fromDate, toDate, page, filters, conditions)
      })
      .catch(e => {
        message.error(
          `There was an error updating the report date: ${e.message}`,
        )
      })
  }

  const columns = [
    {
      title: 'Full Name',
      key: 'id',
      render: (_, row) => {
        return (
          <>
            <Link
              to={toPath.patients.patient({ id: row.patient.id })}
              style={{ whiteSpace: 'nowrap', marginRight: 5 }}
            >
              <Tooltip title="View Patient">
                {`${row.patient.lastName}, ${row.patient.firstName}`}
              </Tooltip>
            </Link>
            <Link
              to={toPath.patients.patient.edit({ id: row.patient.id })}
              title="Edit Patient"
            >
              <Tooltip title="Edit Patient">
                <Icon type="edit" />
              </Tooltip>
            </Link>
          </>
        )
      },
    },
    {
      title: 'MRN',
      dataIndex: 'patient.practiceMrn',
      key: 'MRN',
    },
    {
      title: 'DOB',
      dataIndex: 'patient.birthDate',
      key: 'dob',
      render: formatDOB,
    },
    {
      title: 'Relevant History',
      key: 'diseases',
      render: (_, row) => <DiseasesTags patientSummary={row.patient} />,
    },
    {
      title: 'Device Type',
      dataIndex: 'patient.currentDevice.modelType',
      key: 'deviceType',
    },
    {
      title: 'CPT Code',
      dataIndex: 'patient.currentDevice.inOfficeCptCode',
      key: 'cptCode',
      render: (text, report) =>
        report?.nonBillable ? '' : <span>{text}</span>,
    },
    {
      title: 'ICD-10 Code',
      dataIndex: 'patient.currentDevice.inOfficeIcdCode',
      key: 'icdCode',
      render: (text, report) =>
        report?.nonBillable ? '' : <span>{text}</span>,
    },
    {
      title: 'Report Date',
      key: 'checkDate',
      dataIndex: 'checkDate',
      render: (text, report) => {
        if (!editDates?.[report.id]) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{formatDOB(text)}</span>
              <Tooltip title="Edit Patient">
                <Button
                  type="link"
                  icon="edit"
                  onClick={() =>
                    setEditDates({ ...editDates, [report.id]: text })
                  }
                />
              </Tooltip>
            </div>
          )
        }
        return (
          <div style={{ width: '200px' }}>
            <DatePicker
              value={moment(editDates[report.id])}
              onChange={date =>
                setEditDates({ ...editDates, [report.id]: date })
              }
            />
            <Button
              type="link"
              icon="check"
              onClick={() => handleSaveDate(report)}
            />
            <Button
              type="link"
              icon="close"
              onClick={() => {
                const newDates = { ...editDates }
                delete newDates[report.id]
                setEditDates(newDates)
              }}
            />
          </div>
        )
      },
    },
    {
      title: 'Device Report',
      dataIndex: 'hasDeviceReport',
      key: 'hasDeviceReport',
      align: 'center',
      render(_text, { hasDeviceReport }) {
        return (
          <Icon
            type={`${hasDeviceReport ? 'check' : 'close'}-circle`}
            className={classes[hasDeviceReport ? 'active' : 'notPresent']}
          />
        )
      },
    },
    {
      title: 'Completed',
      dataIndex: 'hasBiobridgeReport',
      key: 'hasBiobridgeReport',
      align: 'center',
      render(_text, { hasBiobridgeReport }) {
        return (
          <Icon
            type={`${hasBiobridgeReport ? 'check' : 'close'}-circle`}
            className={classes[hasBiobridgeReport ? 'active' : 'notPresent']}
          />
        )
      },
    },
    {
      title: 'Signed',
      dataIndex: 'signed',
      key: 'signedAt',
      align: 'center',
      render(_text, { signedAt }) {
        return (
          <Icon
            type={`${signedAt ? 'check' : 'close'}-circle`}
            className={classes[signedAt ? 'active' : 'notPresent']}
          />
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render(_text, report) {
        return (
          <div className={classes.actions}>
            {!report.hasDeviceReport && (
              <>
                <Link to={toEditOfficeCheck(report.id)}>
                  <Tooltip title="Add Device Report">
                    <Button type="link" icon="plus" size="small" />
                  </Tooltip>
                </Link>
                <Tooltip title="Delete Report">
                  <Popconfirm
                    title="Are you sure you want to delete this report?"
                    onConfirm={() => handleDelete(report.id)}
                    okText="Yes"
                    cancelText="No"
                    placement="bottomRight"
                  >
                    <Button type="link" icon="delete" size="small" />
                  </Popconfirm>
                </Tooltip>
              </>
            )}

            {shouldSign(report) && (
              <Link to={toOfficeCheck(report.id)}>
                <Tooltip title="Sign Report">
                  <Button type="link" icon="edit" size="small" />
                </Tooltip>
              </Link>
            )}
            {report.hasDeviceReport && (
              <>
                <Link to={toOfficeCheck(report.id)}>
                  <Tooltip title="View Report">
                    <Button type="link" icon="file-search" size="small" />
                  </Tooltip>
                </Link>
                <Link to={toEditOfficeCheck(report.id)}>
                  <Tooltip title="Edit Report">
                    <Button type="link" icon="edit" size="small" />
                  </Tooltip>
                </Link>
              </>
            )}
          </div>
        )
      },
    },
  ]

  const shouldSign = row => {
    return row.hasBiobridgeReport && canSign && !row.signedAt && !row.isArchived
  }

  const { pageSize, total } = useOfficeChecksPagination(fromDate, toDate)

  const [exportingExcel, exportExcel] = useExportExcel(
    `practices/${practiceId}/office-checks.xlsx?from_date=${fromDate}&to_date=${toDate}`,
  )

  useFeatureGuard('in_office_checks')

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader
        title="Office Checks"
        subTitle={practice?.name}
        extra={[
          (isPractitioner || isAdmin) && (
            <BatchDownloadButton
              key="download"
              practiceId={practiceId}
              fromDate={fromDate}
              toDate={toDate}
            />
          ),
        ]}
      />
      <div className={classes.pageHeader}>
        <RangePicker
          defaultValue={[
            moment(DEFAULT_FILTERS.fromDate),
            moment(DEFAULT_FILTERS.toDate),
          ]}
          className={classes.datePicker}
          ranges={{
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          onChange={handleDateChange}
        />

        <Filters
          showOfficeCheckFilters
          showSearch
          showDeviceType
          showMedicalProvider
          showOfficeLocation
          defaultValues={DEFAULT_FILTERS}
          currentValues={conditions}
          placeholders={{ officeCheckFilters: 'All' }}
          onSubmit={handleStatusFilter}
          className={classes.filters}
        />
        <div className={classes.total}>
          <div className={classes.totalResults}>
            {`${total || 0} result${total !== 1 ? 's' : ''}`}
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={officeChecks || []}
        loading={pending}
        className={classes.officeChecksTable}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
      />
      {Boolean(officeChecks.length > 0) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon="file-excel"
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}
